import {ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {CarService} from "../../services/car.service";
import {
  AbstractProdGrid,
  CustomFilterEvent,
  IProdGrid,
  MercureEvent,
  ProdGridColumnTransforms,
  ProdGridColumnType,
  ProdGridHeaderButton,
  RequestEvent,
  ResponseEvent
} from 'prod-grid';
import {HttpExtraParams, HttpFilterParam, StorageService} from 'prod-shared';
import {DipColumn} from "../../dip/dip-column";
import {CarFormComponent} from "./form/car-form.component";
import {GridMapService} from "../../dip/map/grid-map.service";
import {OrganizationService} from "../../services/organization.service";
import {NationalityService} from "../../services/nationality.service";
import {OrganizationTreeSelect} from "../../dip/organization-tree-select";
import {MercureService} from "../mercure/mercure.service";
import {MercureTopic} from "../mercure/mercure-topic";
import {RoleService} from "../../services/role.service";
import {Role} from "../../dip/role";
import {Md5} from "ts-md5";
import {BatteryStatus} from "./battery-status";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-cars',
  templateUrl: './cars.component.html'
})

export class CarsComponent extends AbstractProdGrid implements IProdGrid, OnDestroy {
  prodGridTitle: string = this.translate.instant('cars.grid_title');
  prodMapTitle: string = this.translate.instant('cars.map_title');
  defaultFilter: HttpFilterParam[] = [new HttpFilterParam({
    property: 'equipment.isDeleted',
    operator: 'eq',
    value: false
  })];
  prodGridToolbarButtonsStatus = {
    isAddButtonDisabled: {
      role: [Role.ROLE_CAR_EDIT],
      disabled: !this.roleService.hasRole([Role.ROLE_CAR_EDIT])
    },
    isRemoveButtonDisabled: {
      role: [Role.ROLE_CAR_DELETE],
      disabled: !this.roleService.hasRole([Role.ROLE_CAR_DELETE])
    }
  };
  override prodGridForm = {
    component: CarFormComponent,
    editStyle: {height: '80%', width: '60%'}
  };
  prodGridColumns: DipColumn[] = [
    DipColumn.get({
      property: 'equipment.lastBatteryEventStatus',
      type: ProdGridColumnType.ICON,
      sortable: false,
      filterable: false,
      transform: {
        transform: ProdGridColumnTransforms.CUSTOM_FUNCTION,
        option: { function: this.statusColumnTransformer, wholeRecord: false }
      }
    }),
    DipColumn.get({
      property: 'equipment.organization.name', type: ProdGridColumnType.SELECT_COMPONENT, selectParameters: {
        selectDataService: OrganizationService,
        isMulti: true,
        isNarrowDown: true,
        isComplete: true
      }
    }),
    DipColumn.get({
        property: 'nationality.code', type: ProdGridColumnType.SELECT_COMPONENT,
        selectParameters: {
          selectDataService: NationalityService,
          isMulti: true,
          isComplete: true,
          isNarrowDown: true
        },
        isEnum: true
      }
    ),
    DipColumn.get({property: 'vin', type: ProdGridColumnType.STRING}),
    DipColumn.get({property: 'numberPlate', type: ProdGridColumnType.STRING}),
    DipColumn.get({
      property: 'equipment.devices.name', type: ProdGridColumnType.STRING, transform: {
        transform: ProdGridColumnTransforms.CUSTOM_FUNCTION,
        option: { function: this.vhmdIdTransformer, wholeRecord: true },
      }
    }),
    DipColumn.get({property: 'equipment.latitude', type: ProdGridColumnType.NUMBER}),
    DipColumn.get({property: 'equipment.longitude', type: ProdGridColumnType.NUMBER}),
    DipColumn.get({property: 'brand', type: ProdGridColumnType.STRING}),
    DipColumn.get({property: 'model', type: ProdGridColumnType.STRING}),
    DipColumn.get({property: 'color', type: ProdGridColumnType.STRING}),
    DipColumn.get({property: 'parkingSpotNumber', type: ProdGridColumnType.STRING}),
    DipColumn.get({
      property: 'parkedAt',
      type: ProdGridColumnType.DATE,
      transform: {
        transform: ProdGridColumnTransforms.DATETIME
      }
    }),
    DipColumn.get({
      property: 'equipment.linkedAt',
      type: ProdGridColumnType.DATE,
      transform: {
        transform: ProdGridColumnTransforms.DATETIME
      }
    }),
    DipColumn.get({
      property: 'equipment.lastBatteryEventAt',
      type: ProdGridColumnType.DATE,
      transform: {
        transform: ProdGridColumnTransforms.DATETIME
      }
    }),
    DipColumn.get({
      property: 'equipment.lastBatteryEventVoltage',
      type: ProdGridColumnType.NUMBER,
      postfix: 'V',
      transform: {transform: ProdGridColumnTransforms.ZERO_PADDING, option: 3}
    }),
    DipColumn.get({
      property: 'createdAt',
      type: ProdGridColumnType.DATE,
      transform: {
        transform: ProdGridColumnTransforms.DATE
      }
    }),
    DipColumn.get({
      property: 'equipment.isDeleted',
      type: ProdGridColumnType.CHECKBOX,
      transform: {
        transform: ProdGridColumnTransforms.TICK
      }
    }),
    DipColumn.get({
      property: 'latLng',
      type: ProdGridColumnType.HIDDEN,
      editable: false
    }),
  ]
  override prodGridHeaderButtons: ProdGridHeaderButton[] = [
    new ProdGridHeaderButton({
      id: 'equipment.lastBatteryEventStatus',
      title: this.translate.instant('cars.header_buttons.alert.tooltip'),
      text: this.translate.instant('cars.header_buttons.alert.text'),
      icon: 'fa fa-battery-1 text-danger',
      clickEvent: this.onClickStatusFilter.bind(this),
      disabled: false,
      isToggleButton: true,
      checked: false,
      value: BatteryStatus.ALERTED
    }),
    new ProdGridHeaderButton({
      id: 'equipment.lastBatteryEventStatus',
      title: this.translate.instant('cars.header_buttons.warning.tooltip'),
      text: this.translate.instant('cars.header_buttons.warning.text'),
      icon: 'fa fa-battery-3 text-warning',
      clickEvent: this.onClickStatusFilter.bind(this),
      disabled: false,
      isToggleButton: true,
      checked: false,
      value: BatteryStatus.WARNED
    }),
    new ProdGridHeaderButton({
      id: 'equipment.lastBatteryEventStatus',
      title: this.translate.instant('cars.header_buttons.ok.tooltip'),
      text: this.translate.instant('cars.header_buttons.ok.text'),
      icon: 'fa fa-battery-full text-success',
      clickEvent: this.onClickStatusFilter.bind(this),
      disabled: false,
      isToggleButton: true,
      checked: false,
      value: BatteryStatus.OK
    }),
    new ProdGridHeaderButton({
      id: 'equipment.lastBatteryEventStatus',
      title: this.translate.instant('cars.header_buttons.unknown.tooltip'),
      text: this.translate.instant('cars.header_buttons.unknown.text'),
      icon: 'fa fa-battery-empty',
      clickEvent: this.onClickStatusFilter.bind(this),
      disabled: false,
      isToggleButton: true,
      checked: false,
      value: BatteryStatus.UNKNOWN
    })
  ];

  splitConfigName = 'angular-split-config';
  previousSplitConfigName = 'angular-split-config-prev';
  splitConfig: { gridSize: number, mapSize: number } = null;
  defaultSplitConfig: { gridSize: number, mapSize: number } = {gridSize: 60, mapSize: 40};
  isMobile: boolean = false;

  @ViewChild('map') map: ElementRef;

  constructor(protected override changeDetectorRef: ChangeDetectorRef,
              override prodGridDataService: CarService,
              public gridMapService: GridMapService,
              public organizationTreeSelect: OrganizationTreeSelect,
              private roleService: RoleService,
              public mercureService: MercureService,
              private storageService: StorageService,
              private translate: TranslateService) {
    super(changeDetectorRef);
    this.organizationTreeSelect.gridComponent = this;
  }

  ngOnDestroy(): void {
    this.mercureService.unsubscribe();
    this.organizationTreeSelect.gridComponent = null;
  }

  private mercureSubscribe() {
    const md5 = new Md5();
    this.mercureService.subscribe(md5.appendStr(`${MercureTopic.EntityChanged}${this.organizationTreeSelect.company.id}`).end().toString()).onmessage = e => this.inputAction$.emit(new MercureEvent([]));
    this.mercureService.subscribe(md5.appendStr(`${MercureTopic.DeviceEvent}${this.organizationTreeSelect.company.id}`).end().toString()).onmessage = e => this.inputAction$.emit(new MercureEvent([]));
  }

  override ngOnInit() {
    super.ngOnInit();

    if (window.screen.availWidth <= 768) this.isMobile = true;

    this.gridMapService.init(this);

    if (this.storageService.get(this.splitConfigName)) {
      this.splitConfig = JSON.parse(this.storageService.get(this.splitConfigName))
    } else {
      this.resetSplitConfig()
    }
  }

  override loadProdGridData(httpExtraParams?: HttpExtraParams): void {
    httpExtraParams = HttpExtraParams.addFilter(httpExtraParams, this.organizationTreeSelect.getHttpExtraParams(this.prodGridDataService));
    super.loadProdGridData(httpExtraParams);
  }

  prodMapEvents(event) {
    this.gridMapService.prodMapEvents(event);
  }

  override outputActionEvents(event: { event: RequestEvent | ResponseEvent, value?: any }) {
    super.outputActionEvents(event);
    this.gridMapService.outputActionEvents(event);
  }

  statusColumnTransformer(lastBatteryEventStatus) {
    let icon = document.createElement('i');
    icon.className = 'fa fa-battery-0';

    switch (lastBatteryEventStatus) {
      case 'OK': {
        icon.className = 'fa fa-battery-4 text-success';
        break;
      }
      case 'ALERTED': {
        icon.className = 'fa fa-battery-1 text-danger';
        break;
      }
      case 'WARNED': {
        icon.className = 'fa fa-battery-3 text-warning';
        break;
      }
    }

    return icon.outerHTML;
  }

  onClickStatusFilter() {
    this.inputAction$.emit(new CustomFilterEvent([new HttpFilterParam({
      property: 'equipment.lastBatteryEventStatus',
      operator: 'in',
      value: this.prodGridHeaderButtons.filter(btn => btn.id === 'equipment.lastBatteryEventStatus').map(btn => {
        return btn.checked ? btn.value : null;
      }).filter(b => b)
    })]));
  }

  resetSplitConfig() {
    this.splitConfig = this.defaultSplitConfig;
    this.storageService.flush(this.splitConfigName);
  }

  onDragEnd(event) {
    this.map['map'].invalidateSize();

    this.splitConfig.gridSize = event.sizes[0];
    this.splitConfig.mapSize = event.sizes[1];

    this.storageService.set(this.splitConfigName, JSON.stringify(this.splitConfig));
  }

  onDblClickGutter() {
    if (this.splitConfig.mapSize !== 0) {
      this.storageService.set(this.previousSplitConfigName, JSON.stringify(this.splitConfig));
      this.splitConfig = {gridSize: 100, mapSize: 0};
    } else {
      this.splitConfig = JSON.parse(this.storageService.get(this.previousSplitConfigName));
    }
    this.storageService.set(this.splitConfigName, JSON.stringify(this.splitConfig));
  }

  vhmdIdTransformer(equipments) {
    if (!equipments.length) return '';
    const dongle = equipments.find(equipment => equipment.deviceType.id === 1);
    return dongle ? dongle.name : '';
  }
}
